// composables/useAlert.ts
import { reactive, readonly } from 'vue'

interface AlertOptions {
  title?: string
  message?: string
  confirmText?: string
  cancelText?: string | null
  type?: 'success' | 'error' | 'warning' | 'info'
  onConfirm?: () => void
  onCancel?: () => void
}

const defaultState = {
  id: 0,
  visible: false,
  title: '',
  message: '',
  confirmText: 'OK',
  cancelText: null as string | null,
  type: 'info' as AlertOptions['type'],
  onConfirm: null as (() => void) | null,
  onCancel: null as (() => void) | null,
}

const state = reactive({ ...defaultState })

/**
 * Show an alert. If `options` is a string we show a simple info alert.
 * If it's an object we merge it *on top of the defaults* so any omitted
 * fields are reset to defaults.
 */
function show(options: string | AlertOptions) {
  // compute next id up-front so every show call creates a fresh key
  const nextId = (state.id || 0) + 1

  if (typeof options === 'string') {
    // use the defaults but ensure id/visible are set
    Object.assign(
      state,
      { ...defaultState, id: nextId, visible: true },
      {
        title: 'Alert',
        message: options,
        confirmText: 'OK',
        cancelText: null,
        type: 'info',
        onConfirm: null,
        onCancel: null,
      },
    )
  } else {
    // merge: start from defaults, then options — this clears leftover fields
    Object.assign(state, { ...defaultState, id: nextId, visible: true }, options)
  }
}

/**
 * Close and reset state after transition. Keep the current id so the key used
 * on the mounted dialog doesn't accidentally get reset to zero.
 */
function close() {
  state.visible = false
  // reset reactive state after transition finishes (match your transition duration)
  setTimeout(() => {
    // preserve the current id value so it remains unique even after reset
    const currentId = state.id
    Object.assign(state, { ...defaultState, id: currentId })
  }, 300)
}
// In useAlert.ts — fix confirm() to use close() so reset runs
function confirm() {
  const handler = state.onConfirm
  close() // sets visible=false AND schedules state reset after 300ms
  try {
    handler?.()
  } catch (e) {
    console.error(e)
  }
}

function cancel() {
  const handler = state.onCancel
  close()
  try {
    handler?.()
  } catch (e) {
    console.error(e)
  }
}

export function useAlert() {
  return {
    state: readonly(state),
    show,
    close,
    confirm,
    cancel,
  }
}
